import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { createCreditProfile } from 'app-components/flux/credit-profile/actions';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';

import { CREDIT_SCORE_REGISTRATION_FORM_DIALOG_ID } from 'components/Dialogs/CreditScore/actions';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import DateField from 'components/Transactions/DateField';
import { CREDIT_SCORE_PROVIDER_ID_EQUIFAX } from 'data/creditProfile/types';
import type { FieldValues } from './CreditScoreRegistrationModal';
import { CreditScoreRegistrationProvider, creditProfileFormHooks, useFormField } from './CreditScoreRegistrationModal';
import MemoTextField from './MemoTextField';
import SSNField from './SSNField';
import styles from './styles';
import type { stateRecord } from './usaStates';
import USA_STATES from './usaStates';

const useStyles = makeStyles()(styles);

type creditProfile = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  consent1?: boolean;
  consent2?: boolean;
  ssn?: string;
};

const initCreditProfile: creditProfile = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  ssn: '',
};

const DateFieldDOB = () => {
  const { value, onValueChange, helperText } = useFormField('dateOfBirth');

  return (
    <DateField
      disableFuture
      label='Date of Birth'
      value={value}
      maxDate={DateTime.now()}
      onBlur={(v: DateTime) => {
        if (!v) {
          onValueChange(undefined);
        }
      }}
      onChange={(v: DateTime<true>) => onValueChange(v.toISODate())}
      error={!!helperText}
      helperText={helperText}
      disabled={value === ''}
      onClick={() => onValueChange(undefined)}
      textFieldProps={{
        style: {
          margin: 0,
          width: '100%',
        },
      }}
      id='credit-score-dob-field'
    />
  );
};

// @ts-expect-error FIXME TS7031
const StateField = ({ classes }) => {
  const { value, onValueChange } = useFormField('state');
  const [selectedState, setSelectedState] = useState<stateRecord | null>();
  const objState = useMemo(() => USA_STATES.find(({ abbreviation }) => value === abbreviation), [value]);

  return (
    <Autocomplete
      disablePortal
      autoHighlight
      openOnFocus
      clearOnBlur
      options={USA_STATES}
      onChange={(_, newValue) => {
        setSelectedState(newValue);
        onValueChange(newValue?.abbreviation);
      }}
      onBlur={() => {
        if (!objState) {
          onValueChange(selectedState?.abbreviation);
        }
      }}
      getOptionLabel={({ name, abbreviation }) => `${name} (${abbreviation})`}
      value={objState}
      renderInput={(textFieldProps) => {
        textFieldProps.inputProps.autoComplete = 'new-password';
        return (
          <MemoTextField
            label='State'
            fieldName='state'
            id='credit-score-first-name-text-field'
            className={classes.addressField}
            textFieldProps={textFieldProps}
          />
        );
      }}
    />
  );
};

type SubmitButtonProps = {
  onClick: (formState: FieldValues) => void;
  disabled?: boolean;
};

const SubmitButton = (props: SubmitButtonProps) => {
  const { onClick, disabled } = props as SubmitButtonProps;
  const formManager = creditProfileFormHooks.useManager();

  return (
    <QButton
      variant='contained'
      onClick={() => formManager.submit(onClick)}
      disabled={disabled}
      id='credit-score-registration-continue-btn'
    >
      Continue
    </QButton>
  );
};

const CreditScoreRegistrationDialog: React.FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);

  const handleContinueClick = (formValues: FieldValues) => {
    const { ssn = '', dateOfBirth, ...restValues } = formValues;
    const formData = {
      creditProviderId: CREDIT_SCORE_PROVIDER_ID_EQUIFAX,
      creditProfile: {
        ...restValues,
        dateOfBirth: dateOfBirth && DateTime.fromISO(dateOfBirth).toFormat('yyyy-MM-dd'),
        ssn: ssn.replaceAll?.('-', ''),
      },
    };
    dispatch(createCreditProfile(formData));
  };

  const handleDialogClose = () => {
    tracker.track(tracker.events.creditScoreReg, { action: 'cancel' });
    dispatch(rootUiActions.removeDialog());
  };

  const renderNameFields = () => (
    <>
      <Grid item xs={6} pt={0}>
        <MemoTextField
          label='First Name'
          fieldName='firstName'
          className={classes.nameField}
          id='credit-score-first-name-text-field'
        />
      </Grid>
      <Grid item xs={6} pt={0}>
        <MemoTextField
          label='Last Name'
          fieldName='lastName'
          className={classes.nameField}
          id='credit-score-last-name-text-field'
        />
      </Grid>
    </>
  );

  const renderAddressFields = () => (
    <>
      <Grid item xs={12}>
        <MemoTextField
          label='Street Address'
          fieldName='addressLine1'
          className={classes.addressField}
          id='credit-score-street-address-text-field'
        />
      </Grid>
      <Grid item xs={6}>
        <MemoTextField
          label='Apartment/Suite Number (Address 2)'
          fieldName='addressLine2'
          className={classes.addressField}
          id='credit-score-suite-number-text-field'
        />
      </Grid>
      <Grid item xs={6}>
        <MemoTextField
          label='City'
          fieldName='city'
          className={classes.addressField}
          id='credit-score-city-text-field'
        />
      </Grid>
      <Grid item xs={6}>
        <StateField classes={classes} />
      </Grid>
      <Grid item xs={6}>
        <MemoTextField
          label='ZIP Code'
          fieldName='zipCode'
          className={classes.addressField}
          id='credit-score-zip-code-text-field'
        />
      </Grid>
    </>
  );

  return (
    <CreditScoreRegistrationProvider input={initCreditProfile}>
      <StdDialog
        open
        size='md'
        showCloseButton
        title='Credit Score'
        disableBackdropClick
        sharedcomponentid={CREDIT_SCORE_REGISTRATION_FORM_DIALOG_ID}
        onClose={handleDialogClose}
      >
        <DialogContent className={classes.registrationDialogContent}>
          <Typography variant='headlineMedium'>Set up credit score reporting (US residents only)</Typography>
          <Typography variant='paragraphRegular' mb={3}>
            Viewing your report does not impact your credit score
          </Typography>
          <Grid container mt={2} spacing={2} columnSpacing={2.5}>
            {renderNameFields()}
            <Grid item xs={6} pt={0}>
              <MemoTextField
                label='Mobile Phone'
                fieldName='phoneNumber'
                className={classes.phoneField}
                id='credit-score-phone-number-text-field'
              />
            </Grid>
            <Grid item xs={6}>
              <DateFieldDOB />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='paragraphRegular' color='text.tertiary'>
                Use the phone number of the person requesting their credit score. A verification link will be sent to
                this phone number.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                name='consent1'
                control={
                  <Checkbox
                    value={consent1}
                    className={classes.checkBoxConsent1}
                    onChange={() => setConsent1((prevValue) => !prevValue)}
                    id='credit-score-processing-consent'
                  />
                }
                label='Yes, I agree that Quicken Inc. and its processing agent(s) may use information 
                        from third-party sources, including my mobile carrier(s), to verify my identity.'
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='paragraphRegular' mt={2}>
                {"If you've moved in the past 6 months, try entering your old address instead of the new one."}
              </Typography>
            </Grid>
            {renderAddressFields()}
            <Grid item xs={12} mt={2}>
              <SSNField />
            </Grid>
          </Grid>
          <FormGroup sx={{ mt: 5, flexDirection: 'row', alignItems: 'center' }}>
            <FormControlLabel
              name='consent1'
              control={
                <Checkbox
                  value={consent2}
                  onChange={() => setConsent2((prevValue) => !prevValue)}
                  id='credit-score-quicken-consent'
                />
              }
              label='Yes, I agree to Credit Score and Monitoring'
            />
            <Typography variant='paragraphRegular' color='primary' fontWeight={500} letterSpacing={1}>
              <Link
                href='https://www.quicken.com/credit-score-terms-of-service'
                underline='none'
                target='_blank'
                rel='noreferrer'
              >
                Terms Of Service
              </Link>
            </Typography>
          </FormGroup>
          <Typography display='block' variant='captionRegular' fontSize={10}>
            By checking “Yes, I agree.” You authorize Quicken Inc. to obtain your credit information from Equifax to
            provide you this service in accordance with the Quicken Credit Score and Monitoring Terms of Service and
            privacy policy.
          </Typography>
          <Typography display='block' variant='captionRegular' fontSize={10} mt={2}>
            The VantageScore provided under the offer described here uses a proprietary credit scoring model designed by
            VantageScore Solutions, LLC. There are numerous other credit scores and models in the marketplace, including
            different VantageScores. Please keep in mind third parties may use a different credit score when evaluating
            your credit worthiness. Also, third parties will take into consideration items other than your credit score
            or information found in your credit file, such as your income. VantageScore®, Equifax®, Experian® and
            TransUnion® are registered trademarks of their respective owners.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SubmitButton disabled={!consent1 || !consent2} onClick={handleContinueClick} />
        </DialogActions>
      </StdDialog>
    </CreditScoreRegistrationProvider>
  );
};

export default CreditScoreRegistrationDialog;
